// src/components/ContactModal.tsx - Contact Form Modal Component
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Close, Send, CheckCircle, CalendarMonth } from '@mui/icons-material';
import axios from 'axios';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  subject?: string;
}

const ContactModal: React.FC<ContactModalProps> = ({ open, onClose, subject = 'General Inquiry - Valuate' }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear errors when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Get the API base URL from environment or use default
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

      // Send the contact form data to the backend
      const response = await axios.post(`${apiBaseUrl}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: formData.message,
        subject: subject,
      });

      if (response.data.success) {
        setSuccess(true);
        // Reset form
        setFormData({ name: '', email: '', company: '', message: '' });

        // Close modal after 2 seconds
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Error sending contact form:', err);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || 'Failed to send message. Please try again.');
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', email: '', company: '', message: '' });
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const fintechColors = {
    accent: '#3B82F6',
    accentLight: '#60A5FA',
    success: '#10B981',
    border: '#334155',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(fintechColors.accent, 0.2)}`,
          boxShadow: `0 20px 60px ${alpha(fintechColors.accent, 0.2)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Typography
          component="div"
          variant="h5"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${fintechColors.accent}, ${fintechColors.accentLight})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {subject.includes('Demo') ? 'Request a Demo' : subject.includes('Platform') ? 'Access Platform' : 'Contact Us'}
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={loading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          {/* Calendly Embed Section */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              background: alpha(fintechColors.accent, 0.05),
              border: `1px solid ${alpha(fintechColors.accent, 0.2)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <CalendarMonth sx={{ color: fintechColors.accent, fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  Schedule a Call
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Book a time that works best for you
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={() => window.open('https://calendly.com/elijahclark-4fhi/30min', '_blank', 'width=800,height=700')}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: fintechColors.accent,
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                boxShadow: `0 4px 20px ${alpha(fintechColors.accent, 0.3)}`,
                '&:hover': {
                  background: fintechColors.accentLight,
                  boxShadow: `0 6px 25px ${alpha(fintechColors.accent, 0.4)}`,
                },
              }}
            >
              Open Calendly Scheduler
            </Button>
          </Box>

          {/* Divider with OR */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
            <Divider sx={{ flex: 1, borderColor: alpha(theme.palette.divider, 0.3) }} />
            <Typography
              variant="body2"
              sx={{
                px: 2,
                color: theme.palette.text.secondary,
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              OR SEND A MESSAGE
            </Typography>
            <Divider sx={{ flex: 1, borderColor: alpha(theme.palette.divider, 0.3) }} />
          </Box>

          {/* Success Message */}
          {success && (
            <Alert
              icon={<CheckCircle />}
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: alpha(fintechColors.success, 0.1),
                border: `1px solid ${alpha(fintechColors.success, 0.3)}`,
              }}
            >
              Message sent successfully! We&apos;ll get back to you soon.
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              name="name"
              label="Full Name"
              variant="outlined"
              fullWidth
              required
              disabled={loading || success}
              value={formData.name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: fintechColors.accent,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: fintechColors.accent,
                  },
                },
              }}
            />
            <TextField
              name="email"
              label="Email Address"
              type="email"
              variant="outlined"
              fullWidth
              required
              disabled={loading || success}
              value={formData.email}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: fintechColors.accent,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: fintechColors.accent,
                  },
                },
              }}
            />
            <TextField
              name="company"
              label="Company Name"
              variant="outlined"
              fullWidth
              disabled={loading || success}
              value={formData.company}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: fintechColors.accent,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: fintechColors.accent,
                  },
                },
              }}
            />
            <TextField
              name="message"
              label="Message"
              variant="outlined"
              fullWidth
              required
              multiline
              rows={4}
              disabled={loading || success}
              value={formData.message}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: fintechColors.accent,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: fintechColors.accent,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 3,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.1),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || success}
            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            sx={{
              borderRadius: 2,
              px: 4,
              background: fintechColors.accent,
              color: '#ffffff',
              fontWeight: 600,
              boxShadow: `0 4px 20px ${alpha(fintechColors.accent, 0.3)}`,
              '&:hover': {
                background: fintechColors.accentLight,
                boxShadow: `0 6px 25px ${alpha(fintechColors.accent, 0.4)}`,
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactModal;
